import { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Button,
  Text,
  VStack,
  HStack,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Box,
  Tooltip,
} from '@chakra-ui/react';
import { useViewer } from '../common/ForgeViewer';
import { AlgorithmSettingsProps } from 'src/components/auto-shapes/types';
import { AutoShapeAlgorithmType, ElementWithProperties } from 'src/gql/graphql';
import { useSelection } from 'src/services/viewer/selection';
import { usePreventClose } from 'src/components/orderContractor/AutoshapePopover';

// Default values (meters)
const DEFAULTS = {
  minRoomHeight: 2.0,
  maxRoomHeight: 5.0,
  minRoomArea: 1.0,
  gridSpacing: 1.0,
};

// Slider configurations
const SLIDER_CONFIGS = {
  minRoomHeight: { min: 0.5, max: 10, step: 0.1, unit: 'm' },
  maxRoomHeight: { min: 1, max: 15, step: 0.5, unit: 'm' },
  minRoomArea: { min: 0.5, max: 50, step: 0.5, unit: 'm²' },
  gridSpacing: { min: 0.25, max: 3, step: 0.25, unit: 'm' },
};

function SliderWithLabel({
  label,
  value,
  min,
  max,
  step,
  onChange,
  displayValue,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
  displayValue?: string;
}) {
  return (
    <VStack align="stretch" spacing={1}>
      <HStack justify="space-between">
        <Text color="gray.500" fontSize="sm">
          {label}
        </Text>
        <Text fontSize="sm" color="gray.600" fontFamily="mono">
          {displayValue ?? value}
        </Text>
      </HStack>
      <Slider
        colorScheme="cyan"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={onChange}
      >
        <SliderTrack>
          <SliderFilledTrack />
        </SliderTrack>
        <SliderThumb boxSize={4} />
      </Slider>
    </VStack>
  );
}

type ParameterSliderProps = {
  label: string;
  tooltip: string;
  value: number;
  defaultValue: number;
  config: { min: number; max: number; step: number; unit: string };
  onChange: (v: number) => void;
};

function ParameterSlider({
  label,
  tooltip,
  value,
  defaultValue,
  config,
  onChange,
}: ParameterSliderProps) {
  return (
    <Tooltip label={tooltip} hasArrow>
      <Box>
        <SliderWithLabel
          label={label}
          value={value ?? defaultValue}
          min={config.min}
          max={config.max}
          step={config.step}
          onChange={onChange}
          displayValue={`${value ?? defaultValue}${config.unit}`}
        />
      </Box>
    </Tooltip>
  );
}

export function RoomDetection3DSettings({
  value,
  onChange,
  onValidityChange,
}: AlgorithmSettingsProps<AutoShapeAlgorithmType.RoomDetection_3D>) {
  const { viewer } = useViewer();
  const { selectedBimElements } = useSelection();
  const [selectedLeafCount, setSelectedLeafCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const setPreventClose = usePreventClose();

  // Prevent closing when settings are rendered (user needs to click in viewer first)
  useEffect(() => {
    if (setPreventClose) {
      setPreventClose(true);
    }
    return () => {
      if (setPreventClose) {
        setPreventClose(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // setPreventClose is stable from useCallback, doesn't need to be in deps

  const modelUrn = useMemo(() => {
    return Object.keys(selectedBimElements)[0];
  }, [selectedBimElements]);

  const fetchLeafDbIds = useCallback(async () => {
    if (!viewer) return;

    setIsLoading(true);
    try {
      const dbIds = selectedBimElements[modelUrn] || [];

      const leafArraysWithProps = await Promise.all(
        dbIds.map(async (dbId) => {
          const parentProps = await new Promise<ElementWithProperties>(
            (resolve) => {
              viewer.model.getProperties(dbId, (props) => {
                const category = props.properties.find(
                  (p) =>
                    p.displayCategory === '__category__' ||
                    p.displayName === 'IfcClass'
                )?.displayValue;

                const material = props.properties
                  .find((p) => p.displayName === 'IfcMaterial')
                  ?.displayValue?.toString()
                  ?.split(';')[0];

                resolve({
                  category: category as string,
                  material: material as string,
                  name: props.name as string,
                  dbId: dbId,
                });
              });
            }
          );

          const leafIds = await getLeafDbIds(viewer, dbId);

          return leafIds.map((leafId) => ({
            ...parentProps,
            dbId: viewer.model.reverseMapDbId(leafId),
          }));
        })
      );

      const flattenedElements = leafArraysWithProps.flat();
      setSelectedLeafCount(flattenedElements.length);
      onChange({
        ...value,
        elements: flattenedElements,
      });
      onValidityChange(flattenedElements.length > 0);
    } catch (error) {
      console.error('Error getting leaf dbIds:', error);
      onValidityChange(false);
    } finally {
      setIsLoading(false);
    }
  }, [
    viewer,
    selectedBimElements,
    modelUrn,
    onChange,
    value,
    onValidityChange,
  ]);

  const updateOption = useCallback(
    (key: string, val: number) => {
      onChange({ ...value, [key]: val });
    },
    [value, onChange]
  );

  return (
    <VStack spacing={3} align="stretch" width="100%">
      {/* Element Selection */}
      <Box
        p={3}
        bg="gray.50"
        _dark={{ bg: 'gray.700' }}
        borderRadius="md"
        borderWidth="1px"
      >
        <HStack justify="space-between" mb={2}>
          <Text fontSize="sm" fontWeight="medium">
            Selected Elements
          </Text>
          <Text fontSize="sm" color="gray.600" fontFamily="mono">
            {selectedLeafCount}
          </Text>
        </HStack>
        <Text fontSize="xs" color="gray.500" mb={2}>
          Select ceiling/roof elements in the 3D viewer
        </Text>
        <Button
          size="sm"
          width="100%"
          onClick={fetchLeafDbIds}
          isLoading={isLoading}
          loadingText="Fetching..."
        >
          Fetch Selected Elements
        </Button>
      </Box>

      {/* Parameters */}
      <Text fontWeight="medium" fontSize="sm" color="gray.700">
        Detection Parameters
      </Text>

      <ParameterSlider
        label="Min Room Height"
        tooltip="Minimum height for a space to be detected as a room"
        value={value.minRoomHeight ?? DEFAULTS.minRoomHeight}
        defaultValue={DEFAULTS.minRoomHeight}
        config={SLIDER_CONFIGS.minRoomHeight}
        onChange={(v) => updateOption('minRoomHeight', v)}
      />

      <ParameterSlider
        label="Max Room Height"
        tooltip="Maximum height for a space to be detected as a room"
        value={value.maxRoomHeight ?? DEFAULTS.maxRoomHeight}
        defaultValue={DEFAULTS.maxRoomHeight}
        config={SLIDER_CONFIGS.maxRoomHeight}
        onChange={(v) => updateOption('maxRoomHeight', v)}
      />

      <ParameterSlider
        label="Min Room Area"
        tooltip="Minimum floor area for a space to be detected as a room"
        value={value.minRoomArea ?? DEFAULTS.minRoomArea}
        defaultValue={DEFAULTS.minRoomArea}
        config={SLIDER_CONFIGS.minRoomArea}
        onChange={(v) => updateOption('minRoomArea', v)}
      />

      <ParameterSlider
        label="Precision"
        tooltip="Grid spacing for sampling. Smaller = more precise but slower"
        value={value.gridSpacing ?? DEFAULTS.gridSpacing}
        defaultValue={DEFAULTS.gridSpacing}
        config={SLIDER_CONFIGS.gridSpacing}
        onChange={(v) => updateOption('gridSpacing', v)}
      />
    </VStack>
  );
}

// Recursively get all leaf dbIds for a given dbId
const getLeafDbIds = async (
  viewer: Autodesk.Viewing.GuiViewer3D,
  dbId: number
): Promise<number[]> => {
  return new Promise((resolve) => {
    viewer.getProperties(dbId, (props) => {
      const childDbIds = props.properties
        .filter((p) => p.displayCategory === '__child__')
        .map((p) => parseInt(p.displayValue as string));

      if (childDbIds.length === 0) {
        resolve([dbId]);
      } else {
        Promise.all(
          childDbIds.map((childId) => getLeafDbIds(viewer, childId))
        ).then((results) => resolve(results.flat()));
      }
    });
  });
};
